// ToolsApp.tsx — a BOARD APP: commercial tools we evaluated, and why.
//
// Sixth of the "Board App" pattern, and the third sibling of OpenSourceApp:
// same 1700x1180 frame, same one-card-with-a-divider chrome, same
// sidebar / gallery / detail shape. If they look like different products
// sitting next to each other on the board, that is a bug.
//
//   LEFT   — search, VERDICT and category filters, the full list.
//   RIGHT  — HOME is a gallery of every tool; picking one swaps the pane for
//            the DETAIL page with our own write-up.
//
// Data: /api/tools (list) + /api/tools/:slug (with body). Body is markdown,
// rendered through the shared blueprint/mdToBlocks.
//
// WHY THIS EXISTS SEPARATELY FROM OPEN SOURCE: commercial closed products do
// not belong in an open-source directory — that is a standing rule. So every
// paid tool Erez sent through had nowhere to land and simply evaporated. Two
// of the last three were closed commercial.
//
// WHY DISQUALIFIER IS THE POINT: for a commercial tool the useful memory is
// rarely "what it does" — it is the ONE specific thing that ruled it out. No
// API. No self-hosting. Licence tier that attaches to the CLIENT's revenue.
// That single line is what stops us re-opening the same evaluation in three
// months, so it gets its own column and its own box on the detail page.
//
// React-Flow traps, same as its siblings: pointerEvents auto plus
// nowheel/nodrag/nopan on everything interactive or scrolling, and useApi() is
// deliberately kept out of the effect deps so it does not refetch forever.

import { useEffect, useMemo, useState } from "react";
import { Loader2, Search, Wrench, ChevronLeft, ExternalLink, Ban } from "lucide-react";
import { useApi } from "@/lib/api";
import { architectureConfig } from "../blueprint/ArchitectureBlocks";
import { BlockRenderer } from "../blueprint/BlockRenderer";
import { bodyToBlocks } from "../blueprint/mdToBlocks";

const T = {
  ink: "#1B211E", ink2: "#4A534D", faint: "#8C948E",
  card: "#FFFFFF", paper: "#FAF7F2", bone: "#F2EDE4",
  line: "#ECE7DF", hair: "#E3DED4",
  green: "#3F8F6F", greenDeep: "#2E6B52", greenWash: "#EEF5F1",
  red: "#B0432E", redWash: "#FBEEEB",
};
const FONT = "'Inter Tight', Inter, system-ui, -apple-system, sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

// Matches AgentBrainApp + SkillsApp + OpenSourceApp + CoursesApp exactly.
const LIST_W = 300;
const MAIN_W = 1400;
const H = 1180;

type Verdict = "in-use" | "adopt" | "watching" | "rejected";

type Tool = {
  id: string;
  slug: string;
  name: string;
  vendor: string | null;
  tagline: string | null;
  url: string | null;
  source: string | null;
  pricing: string | null;
  pricing_model: string | null;
  self_hostable: boolean | null;
  has_api: boolean | null;
  category: string;
  tags: string[];
  verdict: Verdict;
  disqualifier: string | null;
  body_chars: number;
  noticed_at: string;
  updated_at: string;
};
type Detail = Omit<Tool, "body_chars"> & { body: string | null };

// Same spine and same colours as the open-source directory, on purpose — a
// tool and a library are the same KIND of decision.
const VERDICT: Record<Verdict, { label: string; colour: string; wash: string; note: string }> = {
  "in-use":   { label: "in use",   colour: "#2E6B52", wash: "#EEF5F1", note: "we pay for this today" },
  "adopt":    { label: "adopt",    colour: "#2F6BB0", wash: "#EDF3FA", note: "bringing it in" },
  "watching": { label: "watching", colour: "#B07A1E", wash: "#FBF4E6", note: "real, but not for us yet" },
  "rejected": { label: "rejected", colour: "#B0432E", wash: "#FBEEEB", note: "decided against, reason recorded" },
};
const VERDICT_ORDER: Verdict[] = ["in-use", "adopt", "watching", "rejected"];

const chip = (on: boolean): React.CSSProperties => ({
  fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: "0.04em",
  textTransform: "uppercase", cursor: "pointer", pointerEvents: "auto",
  color: on ? T.greenDeep : T.faint,
  background: on ? T.greenWash : "transparent",
  border: `1px solid ${on ? T.green : T.hair}`,
  borderRadius: 8, padding: "4px 10px",
});

// Two booleans that decide most of our build/buy calls, so they get shown
// plainly rather than buried in prose. null means we never established it.
function capability(t: { self_hostable: boolean | null; has_api: boolean | null }): string {
  const bits: string[] = [];
  if (t.has_api === true) bits.push("has an API");
  else if (t.has_api === false) bits.push("NO API");
  if (t.self_hostable === true) bits.push("self-hostable");
  else if (t.self_hostable === false) bits.push("cloud only");
  return bits.join("  ·  ");
}

export function ToolsApp() {
  const api = useApi();
  const [items, setItems] = useState<Tool[] | "loading">("loading");
  const [slug, setSlug] = useState<string | null>(null);
  const [detail, setDetail] = useState<Detail | "loading" | null>(null);

  const [q, setQ] = useState("");
  const [verdict, setVerdict] = useState<"all" | Verdict>("all");
  const [cat, setCat] = useState<string>("all");

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const r = await api("/api/tools");
        const rows = (await r.json()) as Tool[];
        if (!dead) setItems(rows);
      } catch { if (!dead) setItems([]); }
    })();
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!slug) { setDetail(null); return; }
    let dead = false;
    setDetail("loading");
    (async () => {
      try {
        const r = await api(`/api/tools/${slug}`);
        const row = (await r.json()) as Detail;
        if (!dead) setDetail(row);
      } catch { if (!dead) setDetail(null); }
    })();
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const shown = useMemo(() => {
    if (items === "loading") return [];
    const needle = q.trim().toLowerCase();
    return items.filter(t => {
      if (verdict !== "all" && t.verdict !== verdict) return false;
      if (cat !== "all" && t.category !== cat) return false;
      if (!needle) return true;
      return (
        t.name.toLowerCase().includes(needle) ||
        (t.tagline || "").toLowerCase().includes(needle) ||
        (t.vendor || "").toLowerCase().includes(needle) ||
        t.tags.some(x => x.toLowerCase().includes(needle))
      );
    });
  }, [items, q, verdict, cat]);

  const cats = useMemo(() => {
    if (items === "loading") return [];
    return [...new Set(items.map(t => t.category))].sort();
  }, [items]);

  const counts = useMemo(() => {
    if (items === "loading") return {} as Record<string, number>;
    return items.reduce<Record<string, number>>((a, t) => {
      a[t.verdict] = (a[t.verdict] ?? 0) + 1; return a;
    }, {});
  }, [items]);

  const loading = (
    <div style={{ padding: 26, display: "inline-flex", alignItems: "center", gap: 8, fontFamily: FONT, fontSize: 13, color: T.faint }}>
      <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} /> Loading…
    </div>
  );

  return (
    <div
      className="nopan nodrag"
      style={{ display: "flex", width: LIST_W + MAIN_W, height: H, fontFamily: FONT, pointerEvents: "auto", background: T.card, border: `1px solid ${T.hair}`, borderRadius: 20, overflow: "hidden", boxShadow: "0 8px 24px rgba(25,29,27,0.05)" }}
    >
      {/* ── SIDEBAR ─────────────────────────────────────────── */}
      <aside
        className="nowheel nodrag nopan"
        style={{ width: LIST_W, flexShrink: 0, overflow: "auto", background: T.paper, borderRight: `1px solid ${T.line}`, padding: "22px 18px 26px", pointerEvents: "auto", boxSizing: "border-box" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <Wrench style={{ width: 15, height: 15, color: T.green }} />
          <span style={{ fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.greenDeep }}>
            Tools
          </span>
        </div>
        <div style={{ fontSize: 12, color: T.faint, marginBottom: 16 }}>
          {items === "loading" ? "…" : `${shown.length} of ${items.length} · commercial products, and why`}
        </div>

        <div style={{ position: "relative", marginBottom: 14 }}>
          <Search style={{ width: 13, height: 13, color: T.faint, position: "absolute", left: 11, top: 10 }} />
          <input
            className="nodrag nopan" value={q} onChange={e => setQ(e.target.value)}
            onPointerDown={e => e.stopPropagation()} placeholder="Search tools…"
            style={{ fontFamily: FONT, fontSize: 13, color: T.ink, background: T.card, border: `1px solid ${T.hair}`, borderRadius: 9, padding: "8px 12px 8px 32px", width: "100%", boxSizing: "border-box", outline: "none", pointerEvents: "auto" }}
          />
        </div>

        {/* VERDICT — the spine */}
        <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: 14 }}>
          <button className="nodrag nopan" style={chip(verdict === "all")}
            onPointerDown={e => e.stopPropagation()} onClick={() => setVerdict("all")}>
            all verdicts
          </button>
          {VERDICT_ORDER.filter(v => counts[v]).map(v => {
            const on = verdict === v, s = VERDICT[v];
            return (
              <button key={v} className="nodrag nopan"
                onPointerDown={e => e.stopPropagation()} onClick={() => setVerdict(v)}
                style={{ textAlign: "left", cursor: "pointer", pointerEvents: "auto", fontFamily: FONT, fontSize: 12, fontWeight: 600, color: on ? T.card : s.colour, background: on ? s.colour : s.wash, border: `1px solid ${s.colour}`, borderRadius: 9, padding: "6px 9px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
                <span>{s.label}</span>
                <span style={{ fontFamily: MONO, fontSize: 10, opacity: 0.85 }}>{counts[v]}</span>
              </button>
            );
          })}
        </div>

        {/* category */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 18 }}>
          <button className="nodrag nopan" style={chip(cat === "all")}
            onPointerDown={e => e.stopPropagation()} onClick={() => setCat("all")}>
            all areas
          </button>
          {cats.map(c => (
            <button key={c} className="nodrag nopan" style={chip(cat === c)}
              onPointerDown={e => e.stopPropagation()} onClick={() => setCat(c)}>
              {c}
            </button>
          ))}
        </div>

        {slug && (
          <button className="nodrag nopan" onPointerDown={e => e.stopPropagation()} onClick={() => setSlug(null)}
            style={{ display: "inline-flex", alignItems: "center", gap: 5, fontFamily: FONT, fontSize: 12, fontWeight: 600, color: T.greenDeep, background: "transparent", border: "none", cursor: "pointer", pointerEvents: "auto", padding: 0, marginBottom: 14 }}>
            <ChevronLeft style={{ width: 13, height: 13 }} /> All tools
          </button>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {shown.map(t => {
            const on = t.slug === slug;
            return (
              <button key={t.id} className="nodrag nopan"
                onPointerDown={e => e.stopPropagation()} onClick={() => setSlug(t.slug)}
                style={{ textAlign: "left", cursor: "pointer", pointerEvents: "auto", background: on ? T.card : "transparent", border: `1px solid ${on ? T.hair : "transparent"}`, borderLeft: `2px solid ${on ? VERDICT[t.verdict].colour : "transparent"}`, borderRadius: 8, padding: "8px 10px", fontFamily: FONT }}>
                <div style={{ fontSize: 13, fontWeight: on ? 600 : 500, color: T.ink, lineHeight: 1.3 }}>{t.name}</div>
                <div style={{ fontFamily: MONO, fontSize: 10, color: VERDICT[t.verdict].colour, marginTop: 2 }}>
                  {VERDICT[t.verdict].label}{t.vendor ? ` · ${t.vendor}` : ""}
                </div>
              </button>
            );
          })}
          {items !== "loading" && shown.length === 0 && (
            <div style={{ fontSize: 12, color: T.faint, padding: "10px 2px" }}>Nothing matches.</div>
          )}
        </div>
      </aside>

      {/* ── MAIN ────────────────────────────────────────────── */}
      <main
        className="nowheel nodrag nopan"
        style={{ flex: 1, minWidth: 0, overflow: "auto", background: T.card, pointerEvents: "auto", boxSizing: "border-box" }}
      >
        {items === "loading" ? loading : slug ? (
          detail === "loading" ? loading : !detail ? (
            <div style={{ padding: 26, fontSize: 13, color: T.faint }}>Could not load that tool.</div>
          ) : (
            <div style={{ padding: "34px 40px 56px" }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <span style={{ fontFamily: MONO, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.card, background: VERDICT[detail.verdict].colour, borderRadius: 6, padding: "3px 8px" }}>
                  {VERDICT[detail.verdict].label}
                </span>
                <span style={{ fontSize: 12, color: T.faint }}>{VERDICT[detail.verdict].note}</span>
              </div>
              <h2 style={{ fontSize: 30, fontWeight: 700, color: T.ink, margin: "0 0 10px", letterSpacing: "-0.02em", lineHeight: 1.15 }}>
                {detail.name}
              </h2>
              {detail.tagline && (
                <p style={{ fontSize: 15.5, color: T.ink2, lineHeight: 1.6, margin: "0 0 12px", maxWidth: "72ch" }}>
                  {detail.tagline}
                </p>
              )}
              <div style={{ fontFamily: MONO, fontSize: 11, color: T.faint, marginBottom: 14 }}>
                {[detail.vendor, detail.category, detail.pricing, capability(detail)].filter(Boolean).join("  ·  ")}
              </div>

              {/* The disqualifier is the whole point of the row. */}
              {detail.disqualifier && (
                <div style={{ background: T.redWash, border: `1px solid ${T.red}`, borderRadius: 12, padding: "13px 16px", marginBottom: 20, maxWidth: "80ch" }}>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
                    <Ban style={{ width: 12, height: 12, color: T.red }} />
                    <span style={{ fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.red }}>
                      What rules it out
                    </span>
                  </div>
                  <div style={{ fontSize: 14, color: T.ink, lineHeight: 1.55 }}>{detail.disqualifier}</div>
                </div>
              )}

              {detail.url && (
                <div style={{ display: "flex", gap: 14, marginBottom: 14 }}>
                  <a href={detail.url} target="_blank" rel="noreferrer"
                    style={{ display: "inline-flex", alignItems: "center", gap: 5, fontFamily: FONT, fontSize: 12.5, fontWeight: 600, color: T.greenDeep, textDecoration: "none", pointerEvents: "auto" }}>
                    <ExternalLink style={{ width: 12, height: 12 }} /> Product site
                  </a>
                </div>
              )}
              {detail.source && (
                <div style={{ fontFamily: MONO, fontSize: 10.5, color: T.faint, marginBottom: 20 }}>
                  Found via {detail.source}
                </div>
              )}

              <div style={{ borderTop: `1px solid ${T.line}`, paddingTop: 24 }}>
                {(() => {
                  const blocks = bodyToBlocks(detail.body);
                  return blocks && blocks.length
                    ? <BlockRenderer config={architectureConfig} blocks={blocks} />
                    : <div style={{ fontSize: 13, color: T.faint }}>No write-up yet.</div>;
                })()}
              </div>
            </div>
          )
        ) : (
          <div style={{ padding: "30px 32px 40px" }}>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: T.ink, margin: "0 0 4px", letterSpacing: "-0.01em" }}>
              Commercial tools, and why
            </h2>
            <p style={{ fontSize: 13, color: T.ink2, margin: "0 0 24px", maxWidth: "78ch" }}>
              Closed products don't belong in the open-source directory, so they used to evaporate. Every row here
              carries a verdict and — where we said no — the one specific thing that ruled it out. That single line
              is what stops us re-opening the same evaluation in three months.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(262px, 1fr))", gap: 14 }}>
              {shown.map(t => {
                const v = VERDICT[t.verdict];
                return (
                  <button key={t.id} className="nodrag nopan"
                    onPointerDown={e => e.stopPropagation()} onClick={() => setSlug(t.slug)}
                    style={{ textAlign: "left", cursor: "pointer", pointerEvents: "auto", background: T.paper, border: `1px solid ${T.hair}`, borderTop: `3px solid ${v.colour}`, borderRadius: 14, padding: "14px 16px", fontFamily: FONT, display: "flex", flexDirection: "column", gap: 7, minHeight: 168 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                      <span style={{ fontFamily: MONO, fontSize: 9.5, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: v.colour, background: v.wash, border: `1px solid ${v.colour}`, borderRadius: 5, padding: "2px 6px" }}>
                        {v.label}
                      </span>
                      {t.has_api === false && (
                        <span style={{ fontFamily: MONO, fontSize: 9.5, color: T.red }}>no API</span>
                      )}
                    </div>
                    <div style={{ fontSize: 14.5, fontWeight: 600, color: T.ink, lineHeight: 1.28 }}>{t.name}</div>
                    <div style={{ fontSize: 12, color: T.ink2, lineHeight: 1.5, flex: 1 }}>
                      {(t.tagline || "").slice(0, 112)}{(t.tagline || "").length > 112 ? "…" : ""}
                    </div>
                    {t.disqualifier && (
                      <div style={{ fontSize: 11.5, color: T.red, lineHeight: 1.45, background: T.redWash, border: `1px solid ${T.hair}`, borderRadius: 7, padding: "5px 8px" }}>
                        {t.disqualifier.slice(0, 96)}{t.disqualifier.length > 96 ? "…" : ""}
                      </div>
                    )}
                    <div style={{ fontFamily: MONO, fontSize: 10, color: T.faint, borderTop: `1px solid ${T.line}`, paddingTop: 7 }}>
                      {[t.vendor, t.pricing].filter(Boolean).join("  ·  ") || "pricing not recorded"}
                    </div>
                  </button>
                );
              })}
            </div>
            {shown.length === 0 && (
              <div style={{ fontSize: 13, color: T.faint, paddingTop: 12 }}>Nothing matches those filters.</div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default ToolsApp;
